import { useMemo, useState, type ChangeEvent } from 'react'
import { fieldKeys, type CatalogRow, type ColumnMapping, type PricingRules, type RawRow } from './domain/catalog'
import { formatMoney } from './domain/pricing'
import { buildCatalogRows } from './domain/validation'
import { buildIssueReport, buildWooCommerceCsv, downloadTextFile, getExportableRows } from './export/woocommerce'
import { parseCatalogFile } from './ingest/files'
import { detectMapping } from './mapping/mapping'
import { salesConfig } from './config'
import { useLicense } from './licensing'
import { LicenseSection } from './Sales'

const fieldLabels: Record<(typeof fieldKeys)[number], string> = {
  sku: 'SKU / product code',
  name: 'Product name',
  price: 'Supplier price',
  stock: 'Stock quantity',
  category: 'Category',
  image: 'Image URL',
}

const defaultPricing: PricingRules = {
  markupPercent: 30,
  taxPercent: 0,
  roundingIncrement: 1,
}

export function App() {
  const license = useLicense()
  const [fileName, setFileName] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [rawRows, setRawRows] = useState<RawRow[]>([])
  const [mapping, setMapping] = useState<ColumnMapping | null>(null)
  const [processedMapping, setProcessedMapping] = useState<ColumnMapping | null>(null)
  const [pricing, setPricing] = useState<PricingRules>(defaultPricing)
  const [rows, setRows] = useState<CatalogRow[]>([])
  const [includedWarningRows, setIncludedWarningRows] = useState<Set<number>>(new Set())
  const [reviewPage, setReviewPage] = useState(0)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isDemoLoading, setIsDemoLoading] = useState(false)

  const summary = useMemo(
    () => ({
      total: rows.length,
      valid: rows.filter((row) => row.status === 'valid').length,
      warnings: rows.filter((row) => row.status === 'warning').length,
      excluded: rows.filter((row) => row.status === 'excluded').length,
    }),
    [rows],
  )

  const exportableRows = useMemo(
    () => getExportableRows(rows, includedWarningRows),
    [rows, includedWarningRows],
  )

  const reviewPageSize = 250
  const visibleRows = rows.slice(reviewPage * reviewPageSize, (reviewPage + 1) * reviewPageSize)
  const exportLocked = salesConfig.enforcePaidAccess && !license.isActive && rows.length > salesConfig.freeRowLimit

  const invalidateReview = () => {
    setProcessedMapping(null)
    setRows([])
    setIncludedWarningRows(new Set())
    setReviewPage(0)
  }

  const loadFile = async (file: File, runReview = false) => {
    const parsed = await parseCatalogFile(file)
    const detectedMapping = detectMapping(parsed.headers)
    setFileName(file.name)
    setHeaders(parsed.headers)
    setRawRows(parsed.rows)
    setMapping(detectedMapping)
    setProcessedMapping(runReview ? detectedMapping : null)
    setRows(runReview ? buildCatalogRows(parsed.rows, detectedMapping, pricing) : [])
    setIncludedWarningRows(new Set())
    setReviewPage(0)
  }

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setError('')
    setIsLoading(true)
    try {
      await loadFile(file)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The file could not be read.')
    } finally {
      setIsLoading(false)
      event.target.value = ''
    }
  }

  const runDemo = async () => {
    setError('')
    setIsDemoLoading(true)
    try {
      const response = await fetch('/catalogfix-demo.csv')
      if (!response.ok) throw new Error('The demo catalog is temporarily unavailable.')
      const file = new File([await response.blob()], 'catalogfix-demo.csv', { type: 'text/csv' })
      await loadFile(file, true)
      window.setTimeout(() => document.querySelector('.review-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The demo catalog could not be loaded.')
    } finally {
      setIsDemoLoading(false)
    }
  }

  const processCatalog = () => {
    if (!mapping) return
    if (!mapping.sku || !mapping.name || !mapping.price) {
      setError('Map SKU, product name, and supplier price before continuing.')
      return
    }
    setError('')
    setProcessedMapping({ ...mapping })
    setRows(buildCatalogRows(rawRows, mapping, pricing))
    setIncludedWarningRows(new Set())
    setReviewPage(0)
  }

  const reset = () => {
    setFileName('')
    setHeaders([])
    setRawRows([])
    setMapping(null)
    setProcessedMapping(null)
    setRows([])
    setIncludedWarningRows(new Set())
    setReviewPage(0)
    setError('')
  }

  const toggleWarningRow = (sourceRowNumber: number) => {
    setIncludedWarningRows((current) => {
      const next = new Set(current)
      if (next.has(sourceRowNumber)) next.delete(sourceRowNumber)
      else next.add(sourceRowNumber)
      return next
    })
  }

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="/" aria-label="CatalogFix home">
          <span className="brand-mark" aria-hidden="true"><span>CF</span></span>
          <span className="brand-name">CatalogFix</span>
        </a>
        <nav className="site-nav" aria-label="Primary navigation">
          <a href="/">Home</a>
          <a href="/woocommerce-plugin">WooCommerce Plugin</a>
          <a href="/merchant-audit">Google Feed Audit</a>
          <a href="/guides/validate-woocommerce-product-csv">CSV guide</a>
          <a href="/#pricing">Pricing</a>
          <a href="#license">Activate</a>
          <a href="/privacy">Privacy</a>
          <span className="privacy-pill"><i aria-hidden="true" /> Runs locally</span>
        </nav>
      </header>

      <main id="top" className="demo-main">
        <aside className="product-switch" aria-label="CatalogFix product selector">
          <div><span>Current tool</span><strong>WooCommerce CSV Prep &amp; Export</strong><small>Clean a supplier file and prepare a CSV for manual WooCommerce import.</small></div>
          <a href="/merchant-audit"><span>Other workflow</span><strong>Google Merchant Feed Audit</strong><small>Validate a product feed before submitting it to Merchant Center.</small></a>
        </aside>
        <section className="demo-intro">
          <div>
            <p className="eyebrow"><span aria-hidden="true" /> Private browser workspace</p>
            <h1>Check a catalog.<br /><em>Before WooCommerce does.</em></h1>
          </div>
          <p>Use a supplier CSV or Excel file, or run the synthetic demo. Nothing is uploaded and CatalogFix never connects to your store.</p>
        </section>

        <section className="demo-workspace" id="catalog-checker">
          <div className="hero-card">
            <div className="card-toolbar">
              <div><i aria-hidden="true" /><span>Catalog health check</span></div>
              <span className="live-state">Ready</span>
            </div>
            <label className="drop-zone">
              <input type="file" accept=".csv,.txt,.xlsx" onChange={onFile} />
              <span className="upload-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none"><path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" /></svg>
              </span>
              <strong>{isLoading ? 'Scanning your file…' : 'Drop your supplier file here'}</strong>
              <small>or click to browse · CSV / XLSX · max 20 MB</small>
              <span className="local-note"><i aria-hidden="true" /> Processed in this browser only</span>
            </label>
            <div className="demo-actions">
              <button className="demo-button" type="button" onClick={runDemo} disabled={isDemoLoading || isLoading}>
                {isDemoLoading ? 'Loading demo…' : 'Run the live demo'} <span aria-hidden="true">→</span>
              </button>
              <a href="/catalogfix-demo.csv" download>Download sample CSV</a>
            </div>
            {fileName && <p className="selected-file">Loaded: <strong>{fileName}</strong> · {rawRows.length.toLocaleString()} rows</p>}
            {error && <p className="error-banner" role="alert">{error}</p>}
          </div>
        </section>

        {mapping && (
          <section className="workspace-section">
            <div className="section-heading">
              <div><p className="step-label">Step 1</p><h2>Confirm column mapping</h2></div>
              <p>Required fields are marked with an asterisk. CatalogFix suggests matches but you stay in control.</p>
            </div>
            <div className="mapping-grid">
              {fieldKeys.map((key) => (
                <label className="field" key={key}>
                  <span>{fieldLabels[key]}{(['sku', 'name', 'price'] as string[]).includes(key) ? ' *' : ''}</span>
                  <select
                    value={mapping[key]}
                    onChange={(event) => {
                      setMapping({ ...mapping, [key]: event.target.value })
                      invalidateReview()
                    }}
                  >
                    <option value="">Not mapped</option>
                    {headers.map((header) => <option value={header} key={header}>{header}</option>)}
                  </select>
                </label>
              ))}
            </div>

            <div className="pricing-panel">
              <div><p className="step-label">Step 2</p><h2>Set pricing rules</h2><p>Retail price = supplier price × markup × tax, then rounded.</p></div>
              <div className="pricing-fields">
                <label className="field"><span>Markup on cost</span><div className="input-suffix"><input type="number" min="0" max="1000" value={pricing.markupPercent} onChange={(event) => { setPricing({ ...pricing, markupPercent: Number(event.target.value) }); invalidateReview() }} /><span>%</span></div></label>
                <label className="field"><span>Tax added</span><div className="input-suffix"><input type="number" min="0" max="100" value={pricing.taxPercent} onChange={(event) => { setPricing({ ...pricing, taxPercent: Number(event.target.value) }); invalidateReview() }} /><span>%</span></div></label>
                <label className="field"><span>Round to</span><select value={pricing.roundingIncrement} onChange={(event) => { setPricing({ ...pricing, roundingIncrement: Number(event.target.value) as PricingRules['roundingIncrement'] }); invalidateReview() }}><option value={1}>0.01</option><option value={5}>0.05</option><option value={10}>0.10</option><option value={100}>1.00</option></select></label>
              </div>
            </div>

            <div className="action-row">
              <button className="button secondary" type="button" onClick={reset}>Start over</button>
              <button className="button primary" type="button" onClick={processCatalog}>Review catalog</button>
            </div>
          </section>
        )}

        {rows.length > 0 && (
          <section className="review-section">
            <div className="section-heading">
              <div><p className="step-label">Step 3</p><h2>Review before export</h2></div>
              <p>Excluded rows never enter the WooCommerce file. Warnings remain visible for manual review.</p>
            </div>
            <div className="summary-grid">
              <article><span>Total rows</span><strong>{summary.total}</strong></article>
              <article className="good"><span>Ready</span><strong>{summary.valid}</strong></article>
              <article className="warn"><span>Warnings</span><strong>{summary.warnings}</strong></article>
              <article className="bad"><span>Excluded</span><strong>{summary.excluded}</strong></article>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Status</th><th>Export</th><th>Source</th><th>SKU</th><th>Name</th><th>Supplier</th><th>Retail</th><th>Stock</th><th>Issues</th></tr></thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={`${row.sourceRowNumber}-${row.sku}`}>
                      <td><span className={`status ${row.status}`}>{row.status}</span></td>
                      <td className="export-choice">
                        {row.status === 'warning' ? (
                          <label>
                            <input
                              type="checkbox"
                              checked={includedWarningRows.has(row.sourceRowNumber)}
                              onChange={() => toggleWarningRow(row.sourceRowNumber)}
                            />
                            <span>Include</span>
                          </label>
                        ) : row.status === 'valid' ? <span>Included</span> : <span>Blocked</span>}
                      </td>
                      <td>Row {row.sourceRowNumber}</td><td>{row.sku || '—'}</td><td>{row.name || '—'}</td>
                      <td>{formatMoney(row.supplierPriceCents)}</td><td>{formatMoney(row.regularPriceCents)}</td><td>{row.stock ?? '—'}</td>
                      <td className="issues-cell">{row.issues.map((issue) => issue.message).join(' ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > reviewPageSize && (
                <div className="table-pagination">
                  <span>Showing rows {(reviewPage * reviewPageSize + 1).toLocaleString()}–{Math.min((reviewPage + 1) * reviewPageSize, rows.length).toLocaleString()} of {rows.length.toLocaleString()}. All rows are validated; only eligible selected rows are exported.</span>
                  <div>
                    <button className="button secondary" type="button" disabled={reviewPage === 0} onClick={() => setReviewPage((page) => page - 1)}>Previous</button>
                    <button className="button secondary" type="button" disabled={(reviewPage + 1) * reviewPageSize >= rows.length} onClick={() => setReviewPage((page) => page + 1)}>Next</button>
                  </div>
                </div>
              )}
            </div>
            <div className="export-panel">
              <div><h3>{exportLocked ? 'Activate Pro to export this catalog' : 'Prepared for manual WooCommerce import review'}</h3><p>{exportLocked ? `The free preview covers up to ${salesConfig.freeRowLimit} rows. Your review is preserved in this tab while you start a trial or activate a license.` : `${exportableRows.length} ${exportableRows.length === 1 ? 'row' : 'rows'} selected. Warning rows stay out unless you explicitly include them. Keep a store backup and review the CSV before importing.`}</p></div>
              <div className="export-actions">
                {rows.some((row) => row.issues.length) && <button className="button secondary" type="button" onClick={() => downloadTextFile(buildIssueReport(rows), 'catalogfix-issues.csv')}>Download issue report</button>}
                {exportLocked && <a className="button secondary" href="#pricing">View Pro plans</a>}
                <button className="button primary" type="button" disabled={exportableRows.length === 0 || !processedMapping || exportLocked} onClick={() => processedMapping && downloadTextFile(buildWooCommerceCsv(exportableRows, processedMapping), 'catalogfix-woocommerce.csv')}>Download WooCommerce CSV</button>
              </div>
            </div>
          </section>
        )}

        <LicenseSection license={license} />
      </main>

      <footer><a className="brand footer-brand" href="/"><span className="brand-mark" aria-hidden="true"><span>CF</span></span><span className="brand-name">CatalogFix</span></a><span><a href="/woocommerce-plugin">WooCommerce Plugin</a> · <a href="/">Home</a> · <a href="/#pricing">Pricing</a> · <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a> · <a href="/refund-policy">Refunds</a></span><span>Private workspace · Not a store backup tool</span></footer>
    </div>
  )
}
